import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useAuth, api } from '../../context/AuthContext';
import MFASetup from './MFASetup';

function AccountSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile State
  const [email, setEmail] = useState(user?.email || '');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // MFA State
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      await api.put('/auth/profile', { email });
      setProfileSuccess('E-Mail-Adresse erfolgreich aktualisiert');
      // Reload user data
      window.location.reload();
    } catch (err) {
      setProfileError(
        err.response?.data?.detail || 'Fehler beim Aktualisieren der E-Mail-Adresse'
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 12) {
      setPasswordError('Das Passwort muss mindestens 12 Zeichen lang sein');
      return;
    }

    // Client-side validation (basic)
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setPasswordError('Das Passwort erfüllt nicht alle Sicherheitsanforderungen (siehe Hinweise unten)');
      return;
    }

    setPasswordLoading(true);

    try {
      await api.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess('Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(
        err.response?.data?.detail || 'Fehler beim Ändern des Passworts'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleMFADisable = async () => {
    if (!window.confirm('Möchten Sie die Zwei-Faktor-Authentifizierung wirklich deaktivieren?')) {
      return;
    }

    setMfaError('');
    setMfaSuccess('');
    setMfaLoading(true);

    try {
      await api.post('/auth/mfa/disable');
      setMfaSuccess('MFA erfolgreich deaktiviert');
      window.location.reload();
    } catch (err) {
      setMfaError(
        err.response?.data?.detail || 'Fehler beim Deaktivieren von MFA'
      );
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMFASuccess = () => {
    setShowMFASetup(false);
    setMfaSuccess('MFA erfolgreich aktiviert!');
    window.location.reload();
  };

  return (
    <div className="account-settings">
      <h2 className="mb-4">Account-Einstellungen</h2>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* Profile Tab */}
        <Tab eventKey="profile" title="Profil">
          <Card className="shadow">
            <Card.Body className="p-4">
              <h5 className="mb-4">Profil-Informationen</h5>

              {profileError && (
                <Alert variant="danger" dismissible onClose={() => setProfileError('')}>
                  {profileError}
                </Alert>
              )}
              {profileSuccess && (
                <Alert variant="success" dismissible onClose={() => setProfileSuccess('')}>
                  {profileSuccess}
                </Alert>
              )}

              <Form onSubmit={handleEmailUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label>Benutzername</Form.Label>
                  <Form.Control
                    type="text"
                    value={user?.username || ''}
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Der Benutzername kann nicht geändert werden
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>E-Mail-Adresse</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="ihre-email@beispiel.de"
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="btn-pcs"
                  disabled={profileLoading || email === user?.email}
                >
                  {profileLoading ? 'Speichern...' : 'E-Mail aktualisieren'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* Password Tab */}
        <Tab eventKey="password" title="Passwort">
          <Card className="shadow">
            <Card.Body className="p-4">
              <h5 className="mb-4">Passwort ändern</h5>

              {passwordError && (
                <Alert variant="danger" dismissible onClose={() => setPasswordError('')}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert variant="success" dismissible onClose={() => setPasswordSuccess('')}>
                  {passwordSuccess}
                </Alert>
              )}

              <Form onSubmit={handlePasswordChange}>
                <Form.Group className="mb-3">
                  <Form.Label>Aktuelles Passwort</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    placeholder="Aktuelles Passwort eingeben"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Neues Passwort</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={12}
                    placeholder="Neues Passwort eingeben"
                  />
                  <Form.Text className="text-muted">
                    <strong>Passwort-Anforderungen (BSI Grundschutz):</strong>
                    <ul className="mb-0 mt-1" style={{fontSize: '0.875em'}}>
                      <li>Mindestens 12 Zeichen</li>
                      <li>Mindestens 1 Großbuchstabe</li>
                      <li>Mindestens 1 Kleinbuchstabe</li>
                      <li>Mindestens 1 Ziffer</li>
                      <li>Mindestens 1 Sonderzeichen (!@#$%^&*...)</li>
                    </ul>
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Passwort bestätigen</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Neues Passwort bestätigen"
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="btn-pcs"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Ändern...' : 'Passwort ändern'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* MFA Tab */}
        <Tab eventKey="mfa" title="Zwei-Faktor-Authentifizierung">
          <Card className="shadow">
            <Card.Body className="p-4">
              <h5 className="mb-4">Zwei-Faktor-Authentifizierung (MFA)</h5>

              {mfaError && (
                <Alert variant="danger" dismissible onClose={() => setMfaError('')}>
                  {mfaError}
                </Alert>
              )}
              {mfaSuccess && (
                <Alert variant="success" dismissible onClose={() => setMfaSuccess('')}>
                  {mfaSuccess}
                </Alert>
              )}

              {showMFASetup ? (
                <MFASetup
                  onSuccess={handleMFASuccess}
                  onCancel={() => setShowMFASetup(false)}
                />
              ) : (
                <>
                  <div className="mb-4">
                    <p>
                      Die Zwei-Faktor-Authentifizierung fügt eine zusätzliche Sicherheitsebene
                      zu Ihrem Account hinzu. Nach der Aktivierung benötigen Sie neben Ihrem
                      Passwort auch einen Code aus Ihrer Authenticator-App.
                    </p>

                    <div className="mt-3">
                      <strong>Status: </strong>
                      {user?.mfa_enabled ? (
                        <span className="text-success">✓ Aktiviert</span>
                      ) : (
                        <span className="text-muted">Nicht aktiviert</span>
                      )}
                    </div>
                  </div>

                  {user?.mfa_enabled ? (
                    <Button
                      variant="danger"
                      onClick={handleMFADisable}
                      disabled={mfaLoading}
                    >
                      {mfaLoading ? 'Deaktivieren...' : 'MFA deaktivieren'}
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      className="btn-pcs"
                      onClick={() => setShowMFASetup(true)}
                    >
                      MFA aktivieren
                    </Button>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}

export default AccountSettings;
